import React, { ImgHTMLAttributes } from "react";

import ZoomIcon from "/public/zoom-icon.svg";

import { styled } from "@mui/material";

import { useElementSize } from "@/hooks/useElementSize";
import { useRelativeMousePosition } from "@/hooks/useRelativeMousePosition";
import { InjectOnLoadSpinner } from "@/lib/inject-on-load-spinner/InjectOnLoadSpinner";
import { returnOnCondition } from "@/utils/conditionalOutput";

import { useSliderContext } from "../../context/Slider.context";

type PropTypes = ImgHTMLAttributes<HTMLImageElement> & {
  isSelected: boolean;
};

export const SliderPhoto: React.FC<PropTypes> = ({
  isSelected,
  src,
  alt,
  ...rest
}) => {
  const ref = React.useRef<HTMLImageElement>(null);
  const { isZoomed, toggleIsZoomed } = useSliderContext();

  const { size } = useElementSize(ref);
  const { coordinatesRatio } = useRelativeMousePosition(ref);
  const presentationStyle: React.CSSProperties = isSelected
    ? {
        top: (size.height * coordinatesRatio.y * -1) / 2,
        left: (size.width * coordinatesRatio.x * -1) / 2,
      }
    : {};

  return (
    <>
      <InjectOnLoadSpinner>
        <Img
          alt={alt}
          src={src}
          ref={ref}
          onMouseDown={(e) => e.preventDefault()}
          onClick={isSelected ? toggleIsZoomed : undefined}
          loading="lazy"
          isSelected={isSelected}
          {...rest}
          data-testid={"sliderImage" + (isSelected ? " current" : "")}
        />
      </InjectOnLoadSpinner>

      {isZoomed && (
        <InjectOnLoadSpinner>
          <PresentationImg
            isZoomed={isSelected && isZoomed}
            alt={alt}
            src={src}
            style={presentationStyle}
            data-testid={isZoomed && "zoomedSliderImage"}
          />
        </InjectOnLoadSpinner>
      )}
    </>
  );
};

type ImgProps = {
  isSelected: boolean;
};

const Img = styled("img")<ImgProps>(({ isSelected = false }) => ({
  borderRadius: "10px",
  maxHeight: "100%",
  marginInline: "auto",
  ...returnOnCondition(
    isSelected,
    { cursor: `${isSelected ? `url(${ZoomIcon.src})` : "pointer"}, zoom-in` },
    { cursor: "pointer" },
  ),
}));

type PresentationImgProps = {
  isZoomed: boolean;
};

const PresentationImg = styled("img")<PresentationImgProps>(({ isZoomed }) => {
  return {
    position: "absolute",
    pointerEvents: "none",
    objectFit: "contain",
    opacity: 0,
    transition: "opacity 0.35s ease",
    transform: "scale(1.5)",
    transformOrigin: "top left",
    ...returnOnCondition(isZoomed, {
      opacity: 1,
    }),

    maxWidth: "100%",
  };
});
